import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { IWarehouse } from 'src/app/@interfaces/warehouse.interface';
import { IWarehouseArticle } from 'src/app/@interfaces/warehouseArticle.interface';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DialogComponent } from '../../dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { InventoryService } from 'src/app/@pages/inventory/inventory.service';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { AlertService } from '@shared-services';
import { InventoryArticleWarningComponent } from '@shared-components';

@Component({
  selector: 'app-warehouse-openings',
  standalone: true,
  templateUrl: './warehouse-openings.component.html',
  styleUrls: ['./warehouse-openings.component.css'],
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    TranslatePipe,
    InventoryArticleWarningComponent,
  ],
})
export class WarehouseOpeningsComponent implements OnInit {
  @Input() article = {} as IArticles;

  private inventoryService = inject(InventoryService);
  private warehouseService = inject(WarehouseService);
  private alertService = inject(AlertService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  warehouseList: IWarehouse[] = [];

  warehouseForm = new FormGroup({
    warehouse: new FormControl({} as IWarehouse, Validators.required),
    value: new FormControl(0),
    quantity: new FormControl(0),
  });

  ngOnInit(): void {
    this.loadWarehouses();
  }

  private loadWarehouses() {
    this.warehouseService
      .listWarehouses()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => (this.warehouseList = result),
        error: (err) => console.error('Error loading warehouses:', err),
      });
  }

  create() {
    const warehouseId = parseInt(
      this.warehouseForm.value.warehouse?.id_almacen.toString() ?? '0',
    );
    const quantity = parseFloat(
      this.warehouseForm.value.quantity?.toString() ?? '0',
    );
    const value = parseFloat(this.warehouseForm.value.value?.toString() ?? '0');

    this.inventoryService
      .setWarehouseOpening(
        this.article.id_articulo,
        warehouseId,
        quantity,
        value,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService
            .open({
              title: this.translate.instant('global.alerts.title.success'),
              message: this.translate.instant(
                'The warehouse was correctly related',
              ),
              type: 'success',
              acceptText: 'Ok',
            })
            .subscribe({
              accept: () => {
                this.updateData();
              },
            });
        },
        error: (error) => {
          this.alertService.open({
            title: this.translate.instant('global.alerts.title.error'),
            message: `${this.translate.instant('Something was wrong')} ${error}`,
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  update(opening: IWarehouseArticle) {
    this.dialog
      .open(DialogComponent, {
        data: { modifyWarehouseOpenings: true, opening },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.updateData(),
      });
  }

  deleteWarehouse(opening: IWarehouseArticle) {
    if (opening.avaliable_quantity !== 0) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.error'),
        message: this.translate.instant('inventory.warehouse.stockDeleteAlert'),
        type: 'error',
        acceptText: 'Ok',
      });
      return;
    }
    this.alertService
      .open({
        title: this.translate.instant('global.alerts.title.info'),
        message: this.translate.instant('You won not be able to revert this!'),
        type: 'info',
        acceptText: 'Ok',
      })
      .subscribe({
        accept: () => {
          this.delete(opening);
        },
      });
  }

  private delete(opening: IWarehouseArticle) {
    this.inventoryService
      .deleteWarehouseOpening(opening.id_warehouse_article)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService
            .open({
              title: this.translate.instant('global.alerts.title.success'),
              message: this.translate.instant(
                'The warehouse was correctly deleted',
              ),
              type: 'success',
              acceptText: 'Ok',
            })
            .subscribe({
              accept: () => {
                this.updateData();
              },
            });
        },
        error: (error) => {
          this.alertService.open({
            title: this.translate.instant('global.alerts.title.error'),
            message: `${this.translate.instant('Something was wrong')} ${error}`,
            type: 'error',
            acceptText: 'Ok',
          });
        },
      });
  }

  private updateData() {
    this.inventoryService
      .getArticle(this.article.id_articulo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => (this.article = result),
        error: (error) =>
          `${this.translate.instant('Something was wrong')} ${error}`,
      });
  }

  textValid(field: string): boolean {
    const control = this.warehouseForm.get(field);
    return !!(control?.invalid && control?.touched);
  }
}
